import {
  APPROVED_CONTRACTOR_INVOICE_STATUSES,
  FILEABLE_CONTRACTOR_INVOICE_STATUSES,
  type ContractorInvoiceStatus,
  type InvoiceValidationStatus,
} from "./constants.js";
import type { InvoiceCheck } from "./invoice-check.js";

// What approving an invoice asks of the admin (CINV-6, CINV-34). One answer for
// the API's guard and every button that approves (CINV-17).

/**
 * Decided for payment — approved, part paid or paid. The flags on it were in
 * front of whoever approved it, so lists read it as reviewed (CINV-34).
 */
export function isDecidedInvoice(status: ContractorInvoiceStatus): boolean {
  return APPROVED_CONTRACTOR_INVOICE_STATUSES.includes(status);
}

/**
 * Whether an admin may still file the invoice under projects — set its own
 * project, tag its lines (CINV-35). Anything but rejected: the filing says which
 * projects it counts towards, not what was approved or paid.
 */
export function canFileInvoice(status: ContractorInvoiceStatus): boolean {
  return FILEABLE_CONTRACTOR_INVOICE_STATUSES.includes(status);
}

/** The checks that stand in the way of a one-click approval: the failing ones. */
export function approvalFlags(checks: readonly InvoiceCheck[]): InvoiceCheck[] {
  return checks.filter((check) => check.result === "MISMATCH");
}

type Verdict = {
  status: InvoiceValidationStatus;
  checks: readonly InvoiceCheck[];
} | null;

/** Whether the verdict has anything an approval goes over: a failing check or a duplicate. */
export function hasApprovalFlags(validation: Verdict): boolean {
  if (!validation) return false;
  return validation.status === "DUPLICATE" || approvalFlags(validation.checks).length > 0;
}

/**
 * Whether approving must say why the flags are fine. An explanation already
 * given answers it (CINV-27); a needs-review alone is a one-click approve.
 */
export function approvalNeedsReason(
  validation: Verdict,
  explanation: string | null
): boolean {
  return explanation === null && hasApprovalFlags(validation);
}
